/**
 * EID-126: pure generator for a transform's compiled ES-module source.
 *
 * Replaces the worker's `new Function($inputs, $query, $outputType,
 * ...aliases, code)` compilation: each code version becomes a real ES module
 * with one `import * as <alias> from '<url>'` statement per `modules` entry
 * and the user code as the body of an exported async function. The worker
 * loads it via `import(blobUrl)` inside a `{type: 'module'}` worker — real
 * line numbers, devtools-debuggable, and module re-imports across code
 * versions are browser module-cache hits by URL (what makes warm recompile
 * cheap).
 *
 * The module aliases move from function parameters to lexical (module-scope)
 * imports; the worker still invokes the default export with
 * ($inputs, $query, $outputType).
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

export interface TransformModuleSource {
  /** The generated ES-module source text. */
  source: string;
  /**
   * Number of generated lines preceding the first user-code line (the import
   * statements plus the `export default` wrapper line). User-code line N
   * (1-based) is at generated line N + codeLineOffset; editors map worker
   * stack-frame lines back with the inverse.
   */
  codeLineOffset: number;
}

// Mirrors the schema's alias pattern (data.json transform.modules
// patternProperties: ^[A-Za-z_$][A-Za-z0-9_$]*$). Validated again here
// (fail-loud) because the alias is interpolated into generated source.
const ALIAS_IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

/**
 * Build the ES-module source for one transform code version.
 *
 * Throws on an alias that is not a plain identifier (schema-invalid input —
 * interpolating it would corrupt or inject into the generated module). URLs
 * are embedded via JSON.stringify so quotes/backslashes cannot break out of
 * the import specifier.
 */
export function buildTransformModuleSource(
  code: string,
  moduleMap: Record<string, string> = {},
): TransformModuleSource {
  const imports = Object.entries(moduleMap).map(([alias, url]) => {
    if (!ALIAS_IDENTIFIER.test(alias)) {
      throw new Error(
        `Invalid module alias '${alias}': must match ${ALIAS_IDENTIFIER}`,
      );
    }
    return `import * as ${alias} from ${JSON.stringify(url)};`;
  });
  const header = [
    ...imports,
    'export default async function ($inputs, $query, $outputType) {',
  ];
  return {
    source: `${header.join('\n')}\n${code}\n}\n`,
    codeLineOffset: header.length,
  };
}
